/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useState } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import { ItemActionDetail, InputDetail, ItemInfo } from './Legend.type';
import { getItemInfo, getRandomId, isEqualItem } from './LegendUtils';

export function useEvents<K>(
  isInteractive: boolean,
  isHideShow: boolean,
  getDetailFromInfo: (info?: ItemInfo) => InputDetail<K>,
  getPrevItemInfo: (info: ItemInfo) => ItemInfo,
  getNextItemInfo: (info: ItemInfo) => ItemInfo,
  onItemAction?: (detail: ItemActionDetail<K>) => void,
  onInput?: (detail: InputDetail<K>) => void
) {
  const [focusedItemInfo, setfocusedItemInfo] = useState<ItemInfo>({
    itemIdx: 0,
    sectionIdx: 0
  });
  const [hoveredItemInfo, sethoveredItemInfo] = useState<ItemInfo>();
  const activeId = useRef<string>();

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const cancelEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const hoverHandler = (event: PointerEvent) => {
    const itemInfo = getItemInfo(event.target as HTMLElement);
    if (!isEqualItem(itemInfo, hoveredItemInfo)) {
      sethoveredItemInfo(itemInfo && { ...itemInfo, isCurrent: true });
      activeId.current = getRandomId();
      if (isInteractive) {
        focusedItemInfo ? setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false }) : '';
        onInput?.(getDetailFromInfo(itemInfo));
      }
    }
  };

  const pointerLeaveHandler = () => {
    sethoveredItemInfo(undefined);
    activeId.current = undefined;
    if (isInteractive) {
      onInput?.(getDetailFromInfo());
    }
  };

  if (!isInteractive) {
    return {
      focusedItemInfo,
      hoveredItemInfo,
      // include pointer leave and pointer move for datatip
      onPointerLeave: pointerLeaveHandler,
      onPointerMove: hoverHandler
    };
  }

  const updatefocusedItemInfo = (itemInfo: ItemInfo) => {
    onInput?.(getDetailFromInfo(itemInfo));
    activeId.current = getRandomId();
    setfocusedItemInfo(itemInfo);
  };

  const handleKeyboardInput = (item: ItemInfo) => {
    if (!isEqualItem(item, focusedItemInfo)) {
      item.isCurrent = true;
      item.isFocusVisible = true;
      if (hoveredItemInfo) {
        sethoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      updatefocusedItemInfo(item);
    }
  };

  const triggerOnAction = () => {
    const { sectionId, itemId } = getDetailFromInfo(focusedItemInfo);
    if (itemId != null) {
      onItemAction?.({ sectionId, itemId });
      activeId.current = getRandomId();
    }
  };

  const pointerUpHandler = (event: PointerEvent) => {
    const itemInfo = getItemInfo(event.target as HTMLElement);
    if (itemInfo != null) {
      setfocusedItemInfo(itemInfo);
      const { sectionId, itemId } = getDetailFromInfo(itemInfo);
      if (itemId != null) {
        onItemAction?.({ sectionId, itemId });
        activeId.current = getRandomId();
      }
    }
  };

  const keyDownHandler = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown': {
        const nextItemInfo = getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        break;
      }
      case 'ArrowUp': {
        const nextItemInfo = getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        break;
      }
      case 'ArrowRight': {
        const nextItemInfo = isRtl
          ? getPrevItemInfo(focusedItemInfo)
          : getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        break;
      }
      case 'ArrowLeft': {
        const nextItemInfo = isRtl
          ? getNextItemInfo(focusedItemInfo)
          : getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        break;
      }
    }
    cancelEvent(event);
  };

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.code;
    switch (key) {
      case 'Space': {
        if (isHideShow) {
          triggerOnAction();
        }
        break;
      }
      case 'Enter': {
        triggerOnAction();
        break;
      }
      case 'Home': {
        updatefocusedItemInfo({
          itemIdx: 0,
          sectionIdx: 0,
          isFocusVisible: true,
          isCurrent: true
        });
        break;
      }
      case 'End': {
        updatefocusedItemInfo({
          itemIdx: -1,
          sectionIdx: -1,
          isCurrent: true,
          isFocusVisible: true
        });
        break;
      }
      case 'Tab': {
        updatefocusedItemInfo({
          ...focusedItemInfo,
          isCurrent: true,
          isFocusVisible: true
        });
        break;
      }
    }
  };

  const blurHandler = () => {
    const itemInfo = { ...focusedItemInfo, isCurrent: false, isFocusVisible: false };
    if (hoveredItemInfo != null || focusedItemInfo.isCurrent) {
      onInput?.(getDetailFromInfo(undefined));
    }
    setfocusedItemInfo(itemInfo);
  };

  return {
    focusedItemInfo,
    hoveredItemInfo,
    'aria-activedescendant': activeId.current,
    onPointerUp: pointerUpHandler,
    onPointerMove: hoverHandler,
    onKeyUp: keyUpHandler,
    onKeyDown: keyDownHandler,
    onPointerLeave: pointerLeaveHandler,
    onBlur: blurHandler
  };
}
